import { createRootRoute, createRoute, createRouter, Link, Navigate, Outlet } from '@tanstack/react-router';
import { z } from 'zod';
import { AppLayout, useCurrentMe } from './components/shell';
import { EmptyState } from './components/ui';
import { AgentDetailPage } from './pages/agent-detail';
import { EditAgentPage, NewAgentPage } from './pages/agent-form';
import { AgentsPage } from './pages/agents';
import { CallbackPage, LoginPage } from './pages/auth';
import { KnowledgePage, KnowledgeSourcePage } from './pages/knowledge';
import { RunDetailPage, RunsPage } from './pages/runs';
import { TeamPage } from './pages/team';
import { TenantsPage } from './pages/tenants';
import { ToolsPage } from './pages/tools';

function NotFoundContent() {
  return (
    <EmptyState
      icon="search"
      title="No encontramos esta página"
      action={
        <Link to="/" className="btn">
          Ir al inicio
        </Link>
      }
    >
      Puede que el enlace esté incompleto o que el recurso ya no exista en este espacio.
    </EmptyState>
  );
}

/** Outside the app shell (e.g. before sign-in). Inside it, the shell keeps its navigation. */
function NotFound() {
  return (
    <main className="auth" data-single>
      <div style={{ width: 'min(560px, 100%)' }}>
        <NotFoundContent />
      </div>
    </main>
  );
}

function IndexRedirect() {
  const me = useCurrentMe();
  return <Navigate to={me.activeTenant ? '/agentes' : '/plataforma/espacios'} replace />;
}

const rootRoute = createRootRoute({ component: Outlet, notFoundComponent: NotFound });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'login',
  validateSearch: z.object({ returnTo: z.string().optional(), error: z.string().optional() }),
  component: LoginPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth/callback',
  component: CallbackPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
});

const routes = [
  createRoute({ getParentRoute: () => appRoute, path: '/', component: IndexRedirect }),
  createRoute({ getParentRoute: () => appRoute, path: 'agentes', component: AgentsPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'agentes/nuevo', component: NewAgentPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'agentes/$agentId', component: AgentDetailPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'agentes/$agentId/editar', component: EditAgentPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'ejecuciones', component: RunsPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'ejecuciones/$runId', component: RunDetailPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'conocimiento', component: KnowledgePage }),
  createRoute({
    getParentRoute: () => appRoute,
    path: 'conocimiento/$sourceId',
    component: KnowledgeSourcePage,
  }),
  createRoute({ getParentRoute: () => appRoute, path: 'herramientas', component: ToolsPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'equipo', component: TeamPage }),
  createRoute({ getParentRoute: () => appRoute, path: 'plataforma/espacios', component: TenantsPage }),
  // Unknown URLs render inside the shell so the navigation stays available.
  createRoute({ getParentRoute: () => appRoute, path: '$', component: NotFoundContent }),
] as const;

const routeTree = rootRoute.addChildren([loginRoute, callbackRoute, appRoute.addChildren(routes)]);

export const router = createRouter({ routeTree, defaultPreload: false, scrollRestoration: true });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
